/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Image,
    Text,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    useBreakpointValue,
    useStyleConfig,
    Button,
    DrawerFooter
} from '@salesforce/retail-react-app/app/components/shared/ui'
import ProductView from '../product-view'
import {useProductViewModal} from '../../hooks/use-product-view-modal'
import {useIntl, FormattedMessage} from 'react-intl'
import {yieldToMain} from '../../utils/common-utils'
import LoadingSpinner from '../loading-spinner'
import {getUrl} from '../../utils/cloudinary-utils'
import ProductViewQuickAdd from '../product-view-quick-add'
import {
    useShopperBasketsMutationHelper,
    useAccessToken,
    useCommerceApi
} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {API_ERROR_MESSAGE} from '../../constants'
import {useToast} from '../../hooks/use-toast'
import {useLocation} from 'react-router-dom'
import {useGlobaleContext} from '../../contexts/globale'
import {useAddToCartDrawerContext} from '../../hooks/use-add-to-cart-drawer'
import {getPriceData} from '../../utils/product-utils'
import {formattedURL} from '../../utils/url'
import CustomApi from '../../hooks/use-custom-api'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {logBasketErrorToSsr} from '../../utils/log-basket-error-to-ssr'
import useTagManager from '../../hooks/use-tag-manager'
import useMonetate from '../../hooks/use-monetate'
import {useCustomerType} from '@salesforce/commerce-sdk-react'
import {useWishList} from '../../hooks/use-wish-list'
/**
 * A Modal that contains Product View
 */
const ProductViewModal = ({
    product,
    isOpen,
    onClose,
    isQuickAddModal = false,
    pwpTargetPromotionId,
    updateCart,
    ...props
}) => {
    const intl = useIntl()
    const location = useLocation()
    const tagManager = useTagManager()
    const monetate = useMonetate()
    const {customerType} = useCustomerType()
    const {data: wishlist, isLoading: isWishlistLoading} = useWishList()
    const wishlistCountRef = useRef(0)
    wishlistCountRef.current = wishlist?.customerProductListItems?.length || 0
    const {setRefreshPrices, currencyCode, countryISO} = useGlobaleContext()
    const {data: basket} = useCurrentBasket()
    const prevIsOpenRef = useRef(isOpen)
    const skipMonetateRestoreRef = useRef(false)
    const restoredMonetateRef = useRef(false)
    const quickviewTrackedRef = useRef(false)
    const monetateRestoreContextRef = useRef({})
    const [showAddedCta, setShowAddedCta] = useState(false)
    const showToast = useToast()
    const showError = (message, statusCode) => {
        showToast({
            title: statusCode === '403' ? message : intl.formatMessage(message),
            status: 'error'
        })
    }
    const productViewModalData = useProductViewModal(product)
    const {
        isOpen: isAddToCartDrawerOpen,
        onOpen: onAddToCartDrawerOpen,
        onClose: onAddToCartDrawerClose
    } = useAddToCartDrawerContext()
    const [actionSpinner, showActionSpinner] = useState(false)
    const [globalePrices, setGlobalePrices] = useState(null)
    const isMobile = useBreakpointValue({base: true, md: false}, {fallback: 'md'})
    const productListStyles = useStyleConfig('ProductListStyles')
    const productViewModalStyles = useStyleConfig('ProductViewModalStyles')
    const productViewStyles = useStyleConfig('ProductView')
    const isOutOfStock = false
    const {getTokenWhenReady} = useAccessToken()
    const api = useCommerceApi()
    const customapi = new CustomApi()
    const {proxy} = api.shopperExperience.clientConfig
    const label = intl.formatMessage(
        {
            defaultMessage: 'Edit modal for {productName}',
            id: 'cart.product_edit_modal.modal_label'
        },
        {productName: productViewModalData?.product?.name}
    )
    const closeDrawer = async () => {
        showActionSpinner(true)
        await yieldToMain()
        handleClose()
        showActionSpinner(false)
    }

    monetateRestoreContextRef.current = {
        basket,
        isAddToCartDrawerOpen
    }

    const restoreMonetatePageIfNeeded = () => {
        if (
            !isQuickAddModal ||
            !quickviewTrackedRef.current ||
            restoredMonetateRef.current ||
            skipMonetateRestoreRef.current ||
            monetateRestoreContextRef.current.isAddToCartDrawerOpen
        ) {
            return
        }
        if (!window.mtPreviousData) {
            return
        }

        const {basket: currentBasket} = monetateRestoreContextRef.current
        const {
            idString,
            breadcrumb,
            customer: isRegistered,
            hits = [],
            searchTerm,
            itemsAdded
        } = window.mtPreviousData

        if (breadcrumb) {
            window.mtBreadcrumb = breadcrumb
        }

        restoredMonetateRef.current = true
        window.monetateQuickviewActive = false
        monetate.monetateTrackPage(
            idString,
            breadcrumb,
            currentBasket?.productItems,
            isRegistered,
            wishlistCountRef.current,
            hits,
            searchTerm,
            itemsAdded,
            currentBasket?.giftCertificateItems
        )
    }

    const handleUpdateCart = updateCart
        ? async (...args) => {
              skipMonetateRestoreRef.current = true
              try {
                  return await updateCart(...args)
              } catch (error) {
                  skipMonetateRestoreRef.current = false
                  throw error
              }
          }
        : undefined

    const handleClose = () => {
        restoreMonetatePageIfNeeded()
        onClose()
    }

    const handleViewDetails = () => {
        skipMonetateRestoreRef.current = true
        window.monetateQuickviewActive = false
        onClose()
    }

    useEffect(() => {
        if (product && isQuickAddModal) {
            setTimeout(() => {
                if (
                    document.querySelectorAll('.pdp-product-details .product-swatch')?.length === 1
                ) {
                    document.querySelector('.pdp-product-details .product-swatch').click()
                }
            }, 700)
        }
    }, [product])
    const quickAddModalProps = isQuickAddModal
        ? {
              sx: {
                  '& .product-name': {
                      fontFamily: 'heading',
                      fontSize: '18px',
                      fontWeight: '400',
                      letterSpacing: '0.09px'
                  },
                  '& .product-view-stack': {
                      gap: '5px'
                  },
                  '& .product-view-stack b': {
                      fontFamily: 'body',
                      fontSize: '14px',
                      letterSpacing: '0.07px',
                      lineHeight: '22px'
                  }
              }
          }
        : {}
    const [productWithFilteredVariants, setProductWithFilteredVariants] = useState(product)
    const [showSpinner, setShowSpinner] = useState(false)
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()

    const handleAddToCart = async () => {
        try {
            setShowSpinner(true)
            await yieldToMain()
            const itemsAdded = await addItemToNewOrExistingBasket([
                {
                    productId: productWithFilteredVariants?.id,
                    price: productWithFilteredVariants?.price,
                    quantity: 1 // hardcode the quantity on the quick add
                }
            ])
            setRefreshPrices(true)
            setShowSpinner(false)
            if (location?.pathname?.indexOf('cart') === -1) {
                const priceData = getPriceData(productWithFilteredVariants)
                const selectedImage = document
                    .querySelector(
                        "div[data-testid='product-view-modal'] .img-wrapper .cloudinary-image"
                    )
                    ?.getAttribute('src')
                setShowAddedCta(true)
                window.setTimeout(() => {
                    setShowAddedCta(false)
                }, 3000)
                setTimeout(() => {
                    skipMonetateRestoreRef.current = true
                    handleClose()
                    onAddToCartDrawerOpen({
                        product: productWithFilteredVariants,
                        itemsAdded,
                        selectedQuantity: 1,
                        priceData,
                        ...(selectedImage && {heroImage: selectedImage})
                    })
                }, 3000)
            } else {
                handleClose()
            }
        } catch (error) {
            setShowSpinner(false)
            const errResponse = error?.response ? await error.response.json() : null
            logger.error('ATCXL Error adding product to basket', {
                namespace: 'ProductViewModal.handleAddToCart',
                additionalProperties: {
                    error,
                    errResponse,
                    productId: productWithFilteredVariants?.id
                }
            })
            logBasketErrorToSsr('ProductViewModal.handleAddToCart', {
                error,
                errResponse,
                productId: productWithFilteredVariants?.id
            })
            if (errResponse?.statusCode === '403') {
                showError(errResponse.statusMessage, errResponse.statusCode)
            } else {
                showError(API_ERROR_MESSAGE)
            }
        }
    }

    useEffect(() => {
        const getGlobalePrices = async () => {
            const token = await getTokenWhenReady()
            const parameters = {
                c_currencyCode: currencyCode,
                c_countryISO: countryISO
            }
            const response = await customapi.post(token, 'globale-prices', '/', parameters, proxy)
            if (response?.bonusPrices) {
                setGlobalePrices(response?.bonusPrices)
            }
        }
        if (!productViewModalData.isFetching && product?.id && pwpTargetPromotionId && isOpen) {
            getGlobalePrices()
        }
    }, [product?.id, productViewModalData.isFetching, pwpTargetPromotionId, isOpen])

    // Close the add-to-cart drawer on route change
    useEffect(() => {
        if (isAddToCartDrawerOpen) {
            onAddToCartDrawerClose()
        }
    }, [location.pathname])
    const desktopModalHeaderProps = isQuickAddModal
        ? productViewModalStyles.desktopModal.header.quickAdd
        : productViewModalStyles.desktopModal.header.edit
    const desktopModalBodyProps = isQuickAddModal
        ? productViewModalStyles.desktopModal.body.quickAdd
        : productViewModalStyles.desktopModal.body.edit
    const desktopProductViewProps = {
        showFullLink: true,
        imageSize: 'sm',
        product: productViewModalData.product,
        isProductLoading: productViewModalData.isFetching,
        isCart: true,
        isQuickAddModal,
        pwpTargetPromotionId,
        globalePrices,
        onModalClose: handleClose,
        ...(isQuickAddModal ? {basket} : {}),
        ...(handleUpdateCart ? {updateCart: handleUpdateCart} : {}),
        ...props
    }
    const closeButton = (
        <Box alignContent="center" borderRadius="50%" onClick={closeDrawer} cursor="pointer">
            <Image
                width="15px"
                height="15px"
                src={getUrl('UIImages/images/crossed-icon.svg')}
                alt="Close"
            />
        </Box>
    )
    useEffect(() => {
        if (isOpen && isQuickAddModal) {
            restoredMonetateRef.current = false
            skipMonetateRestoreRef.current = false
            quickviewTrackedRef.current = false
            window.monetateQuickviewActive = false
        }
    }, [isOpen, isQuickAddModal])

    useEffect(() => {
        if (
            isQuickAddModal &&
            isOpen &&
            productViewModalData.product?.id &&
            !productViewModalData.isFetching &&
            customerType &&
            !(customerType === 'registered' && isWishlistLoading) &&
            !quickviewTrackedRef.current
        ) {
            const breadcrumb = monetate.buildProductBreadcrumbs(productViewModalData.product)
            window.mtBreadcrumb = breadcrumb
            quickviewTrackedRef.current = true
            window.monetateQuickviewActive = true
            monetate.monetateTrackPage(
                'Quickview',
                breadcrumb,
                basket?.productItems,
                customerType === 'registered',
                wishlistCountRef.current,
                [productViewModalData.product],
                null,
                null,
                basket?.giftCertificateItems
            )
        }
    }, [
        isQuickAddModal,
        isOpen,
        productViewModalData.product?.id,
        productViewModalData.product?.name,
        productViewModalData.isFetching,
        customerType,
        isWishlistLoading
    ])

    useEffect(() => {
        if (prevIsOpenRef.current && !isOpen && isQuickAddModal) {
            restoreMonetatePageIfNeeded()
        }
        prevIsOpenRef.current = isOpen
    }, [isOpen, isQuickAddModal, isAddToCartDrawerOpen])

    useEffect(() => {
        return () => {
            if (isQuickAddModal && prevIsOpenRef.current) {
                restoreMonetatePageIfNeeded()
            }
            if (isQuickAddModal) {
                window.monetateQuickviewActive = false
            }
        }
    }, [isQuickAddModal])

    useEffect(() => {
        if (productViewModalData.product && isOpen) {
            window.__dataLayerLoaded = true
            tagManager.productView(
                productViewModalData.product,
                null,
                productViewModalData?.product?.c_driveRecipeID,
                false,
                false
            )
            window.listId = null
            window.__dataLayerLoaded = false
        }
    }, [isOpen])
    return (
        <Box>
            {!isMobile && (
                <Modal
                    size="4xl"
                    isOpen={isOpen}
                    onClose={handleClose}
                    className="product-view-modal"
                    blockScrollOnMount={false}
                    returnFocusOnClose={false}
                >
                    {actionSpinner && <LoadingSpinner position="fixed" top="auto" />}
                    <ModalOverlay className="product-view-overlay" />
                    <ModalContent
                        containerProps={{'data-testid': 'product-view-modal'}}
                        aria-label={label}
                        borderRadius="20px"
                        className="product-view-content"
                        backgroundColor="gray.100"
                        maxW={isQuickAddModal ? '832px' : '900px'}
                    >
                        <>
                            <ModalHeader {...desktopModalHeaderProps}>
                                {isQuickAddModal ? (
                                    <></>
                                ) : (
                                    <Text
                                        color="black.100"
                                        fontSize="17px"
                                        fontWeight="300"
                                        alignContent="center"
                                        lineHeight="26px"
                                        width="fit-content"
                                        letterSpacing="0.085px"
                                    >
                                        <FormattedMessage
                                            id="edit.product"
                                            defaultMessage="Edit Product"
                                        />
                                    </Text>
                                )}
                                {closeButton}
                            </ModalHeader>
                            <ModalBody {...desktopModalBodyProps}>
                                <ProductView {...desktopProductViewProps} />
                            </ModalBody>
                        </>
                    </ModalContent>
                </Modal>
            )}

            {isMobile && (
                <Drawer
                    placement="bottom"
                    size="md"
                    motionPreset="slideInBottom"
                    scrollBehavior="inside"
                    blockScrollOnMount={false}
                    isFullHeight={false}
                    returnFocusOnClose={false}
                    isOpen={isOpen && isMobile}
                    onClose={handleClose}
                    className="product-view-drawer"
                >
                    {actionSpinner && <LoadingSpinner position="fixed" top="auto" />}
                    <DrawerOverlay className="product-view-overlay" />
                    <DrawerContent
                        containerProps={{'data-testid': 'product-view-modal'}}
                        aria-label={label}
                        borderRadius="20px"
                        className="product-view-content"
                        backgroundColor={isQuickAddModal ? 'white' : 'gray.100'}
                        {...productListStyles?.body?.refinementsModal?.content}
                        height={isQuickAddModal ? 'auto' : 'unset'}
                    >
                        <DrawerHeader
                            className="product-view-header custom-border-bottom"
                            padding="0px 0px 10px 0px !important"
                            boxShadow="none"
                            margin="10px 20px 15px 20px"
                        >
                            <Box
                                className="title-drawer"
                                display="flex"
                                justifyContent="space-between"
                            >
                                <Text
                                    color="black.100"
                                    fontSize={isQuickAddModal ? '25px' : '17px'}
                                    fontWeight="300"
                                    alignContent="center"
                                    lineHeight="26px"
                                    fontFamily={isQuickAddModal ? 'heading' : 'body'}
                                    width="fit-content"
                                    letterSpacing="0.085px"
                                >
                                    {isQuickAddModal ? (
                                        <FormattedMessage
                                            id="quick.add"
                                            defaultMessage="Quick Add"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="edit.product"
                                            defaultMessage="Edit Product"
                                        />
                                    )}
                                </Text>
                                {/* Close Button */}
                                <Box
                                    alignContent="center"
                                    borderRadius="50%"
                                    onClick={closeDrawer}
                                    cursor="pointer"
                                >
                                    <Image
                                        width="15px"
                                        height="15px"
                                        src={getUrl('UIImages/images/crossed-icon.svg')}
                                        alt="Close"
                                    />
                                </Box>
                            </Box>
                        </DrawerHeader>
                        <DrawerBody
                            padding="0px 20px 20px 20px"
                            backgroundColor={isQuickAddModal ? 'white' : 'gray.100'}
                            marginTop={0}
                            borderRadius="20px"
                            className="product-view-body"
                            sx={{
                                '.pdp-product-details': {
                                    padding: '0px'
                                },
                                '.pdp-image-gallery-flex': {
                                    gap: '30px'
                                }
                            }}
                        >
                            <Box className="product-view-wrapper" {...quickAddModalProps}>
                                {isQuickAddModal ? (
                                    <ProductViewQuickAdd
                                        showFullLink={true}
                                        imageSize="sm"
                                        product={productViewModalData.product}
                                        isLoading={productViewModalData.isFetching}
                                        isCart={true}
                                        productWithFilteredVariants={productWithFilteredVariants}
                                        setProductWithFilteredVariants={(variant) => {
                                            setProductWithFilteredVariants(variant)
                                        }}
                                        enableFavourite={true}
                                        isQuickAddModal={isQuickAddModal}
                                        showAddToBag={true}
                                        basket={basket}
                                        suppressSwatchSpinner={true}
                                        pwpTargetPromotionId={pwpTargetPromotionId}
                                        globalePrices={globalePrices}
                                        {...props}
                                        {...(handleUpdateCart
                                            ? {updateCart: handleUpdateCart}
                                            : {})}
                                    />
                                ) : (
                                    <ProductView
                                        showFullLink={true}
                                        imageSize="sm"
                                        product={productViewModalData.product}
                                        isProductLoading={productViewModalData.isFetching}
                                        isCart={true}
                                        isQuickAddModal={isQuickAddModal}
                                        pwpTargetPromotionId={pwpTargetPromotionId}
                                        suppressSwatchSpinner={true}
                                        {...props}
                                        {...(handleUpdateCart
                                            ? {updateCart: handleUpdateCart}
                                            : {})}
                                    />
                                )}
                            </Box>
                        </DrawerBody>
                        {isQuickAddModal && (
                            <DrawerFooter
                                padding="20px 0px 0px 0px !important"
                                margin="20px 20px 15px 20px"
                                display="inline-block"
                                textAlign="center"
                                borderTop="1px solid var(--chakra-colors-gray-500)"
                            >
                                <Button
                                    width="full"
                                    borderRadius="90px"
                                    isDisabled={
                                        productWithFilteredVariants?.type?.master ||
                                        !productWithFilteredVariants?.inventory?.orderable
                                    }
                                    isLoading={showSpinner}
                                    onClick={() => {
                                        handleAddToCart(productWithFilteredVariants)
                                    }}
                                    fontSize="17px"
                                    aria-label="Add to Bag"
                                    height="56px"
                                    backgroundColor={'black.100'}
                                    marginBottom={isQuickAddModal ? '10px' : '0px'}
                                >
                                    {showAddedCta && (
                                        <Image
                                            src={getUrl('UIImages/images/tick-circle-white.svg')}
                                            className="tick-icon"
                                            height="14px"
                                            width="14px"
                                            marginRight="5px"
                                            alt="Added to Bag"
                                        />
                                    )}
                                    {showAddedCta ? (
                                        <FormattedMessage
                                            defaultMessage="Added"
                                            id="product_view.button.added_to_cart"
                                        />
                                    ) : isOutOfStock ? (
                                        <FormattedMessage
                                            id="shop_the_look.sold_out"
                                            defaultMessage="Sold Out"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="add.to.bag"
                                            defaultMessage="Add to Bag"
                                        />
                                    )}
                                </Button>
                                <Box
                                    {...formattedURL(productViewModalData.product?.slugUrl)}
                                    {...productViewStyles.viewDetailsLink}
                                    onClick={handleViewDetails}
                                >
                                    <FormattedMessage
                                        id="quick.add.view_details"
                                        defaultMessage="View Details"
                                    />
                                </Box>
                            </DrawerFooter>
                        )}
                    </DrawerContent>
                </Drawer>
            )}
        </Box>
    )
}

ProductViewModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onOpen: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    product: PropTypes.object,
    isLoading: PropTypes.bool,
    isQuickAddModal: PropTypes.bool,
    pwpTargetPromotionId: PropTypes.string,
    updateCart: PropTypes.func
}

export default ProductViewModal
